import React, { useState, FormEvent, ChangeEvent } from "react";
import logo from "@images/sermyble_Logo.png";
import "@styles/loginpageui.css";
import { InputText } from "primereact/inputtext";
import { postLogin } from "@services/api/login/loginService";
import { useAuth } from "context/login/authContext";
import { Link } from "react-router-dom";

const LoginUI: React.FC = () => {
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [Error, setError] = useState<string>("");
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    let isValid = true;

    if (!formData.username) {
      setEmailError("Please enter a valid email address or username.");
      isValid = false;
    } else setEmailError("");

    if (!formData.password) {
      setPasswordError("Password is required.");
      isValid = false;
    } else setPasswordError("");

    if (isValid) {
      setLoading(true);
      try {
        const res = await postLogin(formData);
        if (res && res.token) {
          setLoading(false);
          localStorage.setItem("token", res?.token);
          localStorage.setItem("role", res?.role);
          localStorage.setItem("username", res?.username);
          login();
        } else {
          setLoading(false);
          setError("Invalid username or password");
        }
      } catch (error) {
        setLoading(false);
        setError("Invalid username or password");
      }
    }
  };

  return (
    <div className="login-page">
      {/* ═══ LEFT PANEL — Brand ═══ */}
      <div className="login-brand-panel">
        <div className="brand-bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="brand-content">
          <div className="brand-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#84EEFC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </div>
          <h1 className="brand-title">Sermyble</h1>
          <p className="brand-subtitle">Admin Console</p>
          <div className="brand-features">
            <div className="brand-feature">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#84EEFC" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Real-time food court analytics</span>
            </div>
            <div className="brand-feature">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#84EEFC" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Manage registrations & approvals</span>
            </div>
            <div className="brand-feature">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#84EEFC" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Track orders & payouts</span>
            </div>
          </div>
        </div>
        <p className="brand-footer">© 2026 ASL Information Services</p>
      </div>

      {/* ═══ RIGHT PANEL — Form ═══ */}
      <div className="login-form-panel">
        <div className="login-form-container">
          <div className="login-logo-wrap">
            <img src={logo} className="login-logo" alt="Logo" />
          </div>
          <h2 className="login-title">Welcome back</h2>
          <p className="login-desc">Sign in to your admin account to continue</p>

          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Username</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                <InputText
                  name="username"
                  placeholder="Enter your username"
                  className={`login-input ${emailError ? "error-border" : ""}`}
                  value={formData.username}
                  onChange={handleChange}
                />
              </div>
              {emailError && <span className="field-error">{emailError}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-wrapper">
                <svg className="input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                <InputText
                  name="password"
                  placeholder="Enter your password"
                  className={`login-input ${passwordError ? "error-border" : ""}`}
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                />
                <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  )}
                </button>
              </div>
              {passwordError && <span className="field-error">{passwordError}</span>}
            </div>

            <div className="form-options">
              <label className="remember-label">
                <input type="checkbox" className="remember-checkbox" />
                <span>Remember me</span>
              </label>
              <Link to="/forget_password" className="forgot-link">Forgot password?</Link>
            </div>

            {Error && <div className="login-error-banner">{Error}</div>}

            <button type="submit" className="login-submit-btn" disabled={isLoading}>
              {isLoading ? (
                <span className="btn-loading">
                  <svg className="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                  Signing in...
                </span>
              ) : "Sign In"}
            </button>
          </form>

          <p className="login-footer-text">
            Secure login powered by <strong>Sermyble</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginUI;
